import type { Environment } from "@p0security/shared/types/environment";

import {
  P0_BASTION_HOST_ROLE_BASE_NAME,
  P0_BASTION_HOST_ROLE_PERMISSIONS,
  P0_MANAGEMENT_ROLE_BASE_NAME,
  P0_MANAGEMENT_ROLE_PERMISSIONS,
  toP0ManagementRoleCondition,
  toP0ManagementRoleName,
} from "./permissions.ts";

/**
 * Converts an Azure subscription ID to a scope that can
 * be used to query all resources in the subscription.
 *
 * @param subscriptionId - An Azure subscription ID
 * @returns A scope for the Azure subscription or resource
 *
 * @example
 * ```ts
 * toScope({ subscriptionId: "1234567890" });
 * // returns "subscriptions/1234567890"
 * ```
 *
 * @see https://learn.microsoft.com/en-us/azure/role-based-access-control/scope-overview
 */
export const toScope = (subscriptionId: string) =>
  `/subscriptions/${subscriptionId}`;

const p0AppFor = (environment: Environment) =>
  `p0-security-azure-integration${environment === "dev" ? "-dev" : ""}`;

export const azureAppComponentSetupFor = (environment: Environment) =>
  ({
    appName: p0AppFor(environment),
    credentialInfo: {
      name: "P0Integration",
      issuer: "https://accounts.google.com",
      audiences: ["api://AzureADTokenExchange"],
      description: "P0 integration with Azure",
    },
  }) as const;

export const azureIamWriteComponentSetupFor = (
  id: string,
  clientServicePrincipalId: string
) => {
  const name = toP0ManagementRoleName(P0_MANAGEMENT_ROLE_BASE_NAME, id);
  return {
    customRole: {
      name,
      description: `Custom role for ${name}`,
      actions: P0_MANAGEMENT_ROLE_PERMISSIONS,
      isCustom: true,
      assignableScope: toScope(id),
      condition: toP0ManagementRoleCondition(clientServicePrincipalId),
    },
  } as const;
};

export const azureBastionHostComponentSetupFor = (id: string) => {
  const name = toP0ManagementRoleName(P0_BASTION_HOST_ROLE_BASE_NAME, id);
  return {
    customRole: {
      name: name,
      description: `Custom role for ${name}`,
      actions: P0_BASTION_HOST_ROLE_PERMISSIONS,
      isCustom: true,
      assignableScope: `/subscriptions/${id}`,
    },
  } as const;
};
